<template>
  <div :id="id + '_fs'" :class="_fs_class">
    <cat-label
      :label-id="id + '_lbl'"
      :for-id="textboxId"
      :label="label"
      :optional="!mandatory"
      :display-optional="showOptionals"
      :placeholder="offscreenLabel"
      :errors="errors"
      :readonly="readonly"
      :class="!labelHandled ? 'placeholder' : null"
      :inline="help && !!help.url"
    ></cat-label>
    <cat-help-trigger
      v-if="help && help.url"
      :source-id="id"
      :url="help.url"
      :title="label"
    />
    <cat-hint :id="id + '_hint'" :text="hint" />
    <cat-help
      v-if="help && help.text"
      :id="id"
      :heading="help.title"
      :text="help.text"
      :aria-describedby="label && id ? id + '_lbl' : null"
    />
    <slot></slot>
    <div ref="selectholder" :class="_holder_class" @keydown="handleKeydown">
      <span class="dropdown-indicator" tabindex="-1" aria-hidden="true"></span>
      <select
        :id="id"
        ref="selectElement"
        v-model="model"
        :title="label"
        :aria-required="mandatory"
        :required="errors && errors.length > 0"
        :aria-invalid="errors && errors.length > 0"
        :readonly="readonly"
        :disabled="readonly"
        :aria-labelledby="label ? null : id + '_lbl'"
        :aria-describedby="hint ? id + '_hint' : null"
        :class="{ invalid: errors && errors.length > 0 }"
        data-selectized="true"
        @change="_onChange"
        @blur="_onBlur"
      ></select>
    </div>
    <slot name="after-input"></slot>
    <cat-errors v-if="errors && errors.length > 0" :errors="errors" />
    <div role="presentation" class="spacer"></div>
  </div>
</template>

<script>
import jQuery from "jquery";

import "@/plugins/selectize/plugin_a11y.js";
import "@/plugins/selectize/auto_position.js";

import { defineComponent, inject } from "vue";
import { defaultResolveTxt } from "@/components/field-element";
import CatHelpTrigger from "@/components/catPageTemplate/catHelpTrigger.vue";
import CatErrors from "@/components/catErrors/catErrors.vue";
import CatLabel from "@/components/utilities/cat-label.vue";
import CatHint from "@/components/utilities/hint.vue";
import CatHelp from "@/components/utilities/help.vue";
import SelectMixin from "./select-mixin.js";
import SelectizeMixin from "./selectize-mixin.js";

export default defineComponent({
  setup() {
    // Inject the global value
    const resolveTxt = inject("resolveTxt", defaultResolveTxt);
    const showOptionals = inject("showOptionals", true);

    return {
      resolveTxt,
      showOptionals
    };
  },
  name: "CatAttributeSelectList",
  components: {
    CatErrors,
    CatLabel,
    CatHelpTrigger,
    CatHint,
    CatHelp
  },
  mixins: [SelectMixin, SelectizeMixin],
  props: {
    id: [String],
    errors: [Array],
    label: [String],
    items: [Array],
    hint: [String],
    help: [Object],
    placeholder: [String],
    offscreenLabel: [String],
    labelHandled: {
      type: Boolean,
      default: false,
      description: "Renders a placeholder label if not handled"
    },
    autofillToken: {
      type: String,
      default: "off",
      description:
        "The autofill token to be set on the input text element in the select auto suggest combobox."
    },
    value: [String],
    mandatory: {
      type: Boolean,
      default: false
    },
    size: [String],
    readonly: [Boolean],
    autocompleteSearchingText: [String],
    autocompleteNoResultsText: [String],
    minInputLength: {
      type: Number,
      default: 2
    },
    hideSelected: { type: Boolean, default: false },
    itemId: [String],
    options: {
      type: [Object],
      default: function () {
        return {};
      }
    },
    wrapOverflowSelected: {
      type: Boolean,
      default: false,
      description:
        "If true, wraps the overflow to display the whole selected item content."
    },
    clipOverflowSelected: {
      type: Boolean,
      default: false,
      description:
        "If true, the content is displayed in a single line, like in the default behaviour except that the ellipsis is not shown for the truncated content."
    },
    inlinefields: {
      type: Boolean,
      default: false,
      description: "Style elements beside"
    }
  },
  data: function () {
    return {
      model: this.value ? this.value : "",
      selectize: null,
      textboxId: this.id + "-textbox",
      selectItemId: this.id + "-select-item",
      isFocus: false,
      wasOpen: false,
      lastKey: null,
      isBlankValue: false
    };
  },
  computed: {
    _fs_class: function () {
      return {
        fieldset: true,
        "cat-select-list cat-select-autosuggest": true,
        required: this.mandatory,
        optional: !this.mandatory,
        "readonly-list": this.readonly,
        "autosuggest-select": true,
        "selected-nowrap":
          !this.clipOverflowSelected && !this.wrapOverflowSelected,
        "selected-nowrap-clip":
          this.clipOverflowSelected && !this.wrapOverflowSelected,
        "inline-fields": this.inlinefields
      };
    },
    _holder_class: function () {
      return {
        "select-holder select-list": true,
        small: this.size == "small",
        medium: !this.size || this.size == "medium",
        large: this.size == "large",
        invalid: this.errors && this.errors.length > 0
      };
    }
  },
  watch: {
    value: function (newval, oldvalue) {
      if (this.model != newval) {
        this.model = newval;
        this.selectize.setValue([newval]);
      }
    },
    items: function (newitems, olditems) {
      if (newitems !== olditems) {
        this.selectize.clearOptions();
        for (const option of newitems) {
          this.selectize.addOption(option);
        }
      }
    }
  },

  mounted: function () {
    const _autocompleteChange = this.autocompleteChange;
    const _this = this;
    let settings = {
      plugins: {
        selectize_a11y: {
          openOnClick: true,
          textboxId: this.textboxId,
          selectItemIds: this.itemId ? [this.itemId] : [this.selectItemId],
          autofillToken: this.autofillToken,
          label: this.label || this.offscreenLabel || this.placeholder,
          labelId: this.label ? this.id + "_lbl" : null,
          id: this.id
        },
        auto_position: true
      },
      onChange: (val) => {
        const options = _this.selectize.options;
        const text = options[val] ? options[val].text : null;

        // To prevent emit change when user still on focus
        // Only send event on blur or val has value
        if (window.isTestMode && val === "") {
          // For automated test
          _this.autocompleteClear();
        } else {
          // For normal user
          if (val === "") {
            _this.isBlankValue = true;
          } else {
            _this.isBlankValue = false;
          }
        }

        if (text) {
          _this.autocompleteChange(val, text);
        }
      },
      onFocus: () => {
        _this.isFocus = true;
      },
      onDropdownOpen: () => {
        _this.wasOpen = true;
      },
      onBlur: () => {
        _this.isFocus = false;

        if (_this.wasOpen) {
          const input = _this.$el.querySelector("input[type=select-one]");

          if (input) {
            // Check the user has pressed the TAB key
            if (
              _this.lastKey &&
              _this.lastKey.isShift &&
              _this.lastKey.keycode === 9
            ) {
              _this.focusPreviousElement(input);
            } else if (_this.lastKey && _this.lastKey.keycode === 9) {
              _this.focusNextElement(input);
            } else {
              input.focus(); // Restore focus to the input element
            }
          }

          // Prevents a loop of always setting focus on the input
          _this.wasOpen = false;
        }

        // setTimeout of 1ms to wait onChange to finish before calling onblur
        // This prevents of sending autocompleteClear unnessesary
        setTimeout(() => {
          if (_this.isBlankValue) {
            _this.autocompleteClear();
          }
        }, 1);
      },
      options: this.items,
      render: {
        item: function (item, escape) {
          return (
            '<div class="option">' +
            (item.text
              ? '<span class="name" id="' +
                _this.selectItemId +
                '">' +
                escape(item.text) +
                "</span>"
              : "") +
            "</div>"
          );
        },
        option: function (item, escape) {
          var _item = _this.items[item.$order - 1];
          var label = item.text;
          var avatar =
            typeof _item !== "undefined" && _item.avatar && _item.avatar !== ""
              ? _item.avatar
              : false;
          var extraLines =
            typeof _item !== "undefined" &&
            _item.lines &&
            _item.lines.length > 0
              ? _item.lines
              : false;
          var optionId = _this._formatOptionId(label);
          var optionRow = avatar
            ? '<div class="option avatar" role="option" id="' +
              optionId +
              '"><span class="avatar mrt"><i class="icon icon-' +
              avatar +
              '"></i></span><span class="option-row">'
            : '<div class="option" role="option" id=' +
              optionId +
              '><span  class="option-row">';
          optionRow += `<span class="line ${
            extraLines && extraLines.length > 0 ? "caption" : ""
          }">${escape(label)}</span>`;
          if (extraLines) {
            for (var i = 0; i < extraLines.length; i++) {
              optionRow += `<span class="line line-${i}">${escape(
                extraLines[i]
              )}</span>`;
            }
          }
          optionRow += "</span></div>";
          return optionRow;
        }
      },
      valueField: "value",
      labelField: "text",
      // sortField: [{ field: "text", direction: "asc" }, { field: "$score" }],
      create: false,
      createOnBlur: true,
      persist: false,
      preload: this.minInputLength === 0,
      maxItems: 1,
      hideSelected: this.hideSelected,
      openOnFocus: false,
      placeholder: this.placeholder
    };

    let plgs = {};
    //the core 'settings'
    plgs = jQuery.extend(plgs, settings.plugins);
    //if passed in 'options' contain 'plugins' merge it in to 'plgs'
    if (
      _this.options.plugins &&
      Object.keys(_this.options.plugins).length > 0 &&
      _this.options.plugins.constructor === Object
    ) {
      plgs = jQuery.extend({}, plgs, _this.options.plugins);
    }
    //merge all 'options' and 'settings'
    settings = jQuery.extend(settings, _this.options);
    //now override just 'plugins' with the merged ones 'plgs'
    settings.plugins = plgs;

    const s = jQuery(_this.$refs.selectElement).selectize(settings);
    if (s && s.length > 0) {
      _this.selectize = s[0].selectize;

      if (this.value) {
        _this.selectize.setValue([this.value]);
      }
    }
  },
  methods: {
    _onBlur: function (event) {
      this.$emit("ui-blur", {
        event: event,
        id: this.id
      });
    },
    focusNextElement: function (input) {
      const focussable = this.focusableElements(input);
      const index = focussable.indexOf(input);
      if (index > -1) {
        const nextElement = focussable[index + 1] || focussable[0];
        nextElement.focus();
      }
    },
    focusPreviousElement: function (input) {
      const focussable = this.focusableElements(input);
      const index = focussable.indexOf(input);
      if (index > -1) {
        const nextElement = focussable[index - 1] || focussable[0];
        nextElement.focus();
      }
    },
    focusableElements: function (input) {
      //add all elements we want to include in our selection
      const focussableElements =
        'a:not([disabled]), button:not([disabled]), input[type=text]:not([disabled]), [tabindex]:not([disabled]):not([tabindex="-1"])';
      return Array.prototype.filter.call(
        input.form.querySelectorAll(focussableElements),
        function (element) {
          //check for visibility while always include the current activeElement
          return (
            element.offsetWidth > 0 ||
            element.offsetHeight > 0 ||
            element === document.activeElement
          );
        }
      );
    },
    handleKeydown: function (event) {
      this.lastKey = { keycode: event.keyCode, isShift: event.shiftKey };
    },
    autocompleteChange: function (value, text) {
      if (value == this.model) {
        return;
      }
      this.model = value;
      this._onChange(value, text);
    },
    autocompleteClear: function () {
      this.$emit("ui-clear", {
        id: this.id ? this.id : "",
        el: this.$el
      });
    }
  }
});
</script>
